/**
 * FastVi WebRTC peer wrapper.
 *
 * Compared to the pre-Phase-3 version this:
 *  - Fetches ICE servers (STUN + TURN) dynamically from the backend at call
 *    time instead of hard-coding Google STUN.
 *  - Exposes RTCPeerConnection state (connectionState + iceConnectionState)
 *    via onConnectionState / onIceState callbacks so the UI (and tests) can
 *    react to disconnects and drive ICE restart.
 *  - Supports ICE restart on the caller side (renegotiation for
 *    NAT-rebinding / brief network drops).
 *  - Cleanly stops every local track + closes every RTCRtpSender's
 *    transceiver on destroy so the camera light really goes off.
 */
import axios from "axios";
import { API } from "./api";

// Public fallback used only if the /api/webrtc/ice-servers endpoint fails.
// Keeps calls working (STUN-only) rather than breaking outright.
const FALLBACK_ICE = [{ urls: "stun:stun.l.google.com:19302" }];

export async function fetchIceServers({ callId, isPublic }) {
  try {
    if (isPublic) {
      const { data } = await axios.get(
        `${API}/public/webrtc/ice-servers`,
        { params: { call_id: callId }, withCredentials: false }
      );
      return data.ice_servers?.length ? data.ice_servers : FALLBACK_ICE;
    }
    const { data } = await axios.get(`${API}/webrtc/ice-servers`, {
      withCredentials: true,
    });
    return data.ice_servers?.length ? data.ice_servers : FALLBACK_ICE;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[webrtc] falling back to Google STUN:", err?.message);
    return FALLBACK_ICE;
  }
}

export class CallPeer {
  constructor({
    isCaller,
    signal,
    onRemoteStream,
    onIceState,
    onConnectionState,
    iceServers,
  }) {
    this.isCaller = isCaller;
    this.signal = signal;
    this.onRemoteStream = onRemoteStream;
    this.onIceState = onIceState;
    this.onConnectionState = onConnectionState;
    this.destroyed = false;
    this.localStream = null;
    this.remoteStream = null;

    this.pc = new RTCPeerConnection({
      iceServers: iceServers && iceServers.length ? iceServers : FALLBACK_ICE,
      iceCandidatePoolSize: 4,
      bundlePolicy: "max-bundle",
    });

    this.pc.ontrack = (event) => {
      const [stream] = event.streams;
      if (stream) {
        this.remoteStream = stream;
        if (this.onRemoteStream) this.onRemoteStream(stream);
      }
    };
    this.pc.onicecandidate = (e) => {
      if (e.candidate) this.signal({ type: "ice", candidate: e.candidate });
    };
    this.pc.oniceconnectionstatechange = () => {
      if (this.onIceState) this.onIceState(this.pc.iceConnectionState);
    };
    this.pc.onconnectionstatechange = () => {
      if (this.onConnectionState) this.onConnectionState(this.pc.connectionState);
    };
  }

  async attachLocalStream(stream) {
    this.localStream = stream;
    stream.getTracks().forEach((track) => this.pc.addTrack(track, stream));
  }

  async createOffer() {
    const offer = await this.pc.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: true,
    });
    await this.pc.setLocalDescription(offer);
    this.signal({ type: "offer", sdp: offer });
  }

  /**
   * Attempt an ICE restart. Only valid on the caller side (offerer). On the
   * callee side we wait for the caller's fresh offer.
   */
  async restartIce() {
    if (!this.isCaller) return false;
    if (this.destroyed) return false;
    try {
      // Prefer the standardized API when available.
      if (typeof this.pc.restartIce === "function") this.pc.restartIce();
      const offer = await this.pc.createOffer({ iceRestart: true });
      await this.pc.setLocalDescription(offer);
      this.signal({ type: "offer", sdp: offer, restart: true });
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[webrtc] restartIce failed:", err?.message);
      return false;
    }
  }

  async handleSignal(msg) {
    if (this.destroyed) return;
    if (msg.type === "offer") {
      // In an ICE-restart the caller may send us a new offer mid-call — that
      // is fine, we just answer it again.
      await this.pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
      const answer = await this.pc.createAnswer();
      await this.pc.setLocalDescription(answer);
      this.signal({ type: "answer", sdp: answer });
    } else if (msg.type === "answer") {
      // Ignore stray answers when we're not actually the offerer or when we
      // already have a stable connection with the same description.
      if (this.pc.signalingState === "have-local-offer") {
        await this.pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
      }
    } else if (msg.type === "ice" && msg.candidate) {
      try {
        await this.pc.addIceCandidate(new RTCIceCandidate(msg.candidate));
      } catch {
        /* ignore late ICE */
      }
    }
  }

  destroy() {
    this.destroyed = true;
    try {
      if (this.localStream) {
        this.localStream.getTracks().forEach((t) => {
          try { t.stop(); } catch { /* noop */ }
        });
      }
    } catch { /* noop */ }
    try {
      // Explicitly stop every sender so the OS releases the capture device.
      (this.pc.getSenders?.() || []).forEach((s) => {
        try { s.track && s.track.stop(); } catch { /* noop */ }
      });
    } catch { /* noop */ }
    try { this.pc.close(); } catch { /* noop */ }
    this.localStream = null;
    this.remoteStream = null;
  }
}

export async function getUserStream({ video = true, audio = true } = {}) {
  return navigator.mediaDevices.getUserMedia({ video, audio });
}
